import { useCallback, useEffect, useState } from 'react';
import {
    CONTENT_BODY_FULLSCREEN,
    CONTENT_BODY_HASH_PARAM,
    getHashQueryParam,
    setHashQueryParam,
} from '../utils/hashQueryParams';

function readFullscreenFromHash(): boolean {
    return getHashQueryParam(CONTENT_BODY_HASH_PARAM) === CONTENT_BODY_FULLSCREEN;
}

/**
 * Sync Content Body editor fullscreen with hash ?contentBody=fullScreen (shareable deep link).
 * No-op when `enabled` is false.
 */
export function useContentBodyDeepLink(enabled: boolean): {
    isFullscreen: boolean;
    toggleFullscreen: () => void;
    setFullscreen: (next: boolean) => void;
} {
    const [isFullscreen, setIsFullscreenState] = useState(() => (enabled ? readFullscreenFromHash() : false));

    useEffect(() => {
        if (!enabled) return;
        const syncFromHash = () => {
            setIsFullscreenState(readFullscreenFromHash());
        };
        window.addEventListener('hashchange', syncFromHash);
        return () => window.removeEventListener('hashchange', syncFromHash);
    }, [enabled]);

    const setFullscreen = useCallback(
        (next: boolean) => {
            setIsFullscreenState(next);
            if (!enabled) return;
            setHashQueryParam(CONTENT_BODY_HASH_PARAM, next ? CONTENT_BODY_FULLSCREEN : null);
        },
        [enabled]
    );

    const toggleFullscreen = useCallback(() => {
        setIsFullscreenState((prev) => {
            const next = !prev;
            if (enabled) {
                setHashQueryParam(CONTENT_BODY_HASH_PARAM, next ? CONTENT_BODY_FULLSCREEN : null);
            }
            return next;
        });
    }, [enabled]);

    return { isFullscreen, toggleFullscreen, setFullscreen };
}
